
import { computed, defineComponent, PropType } from "vue";
import TableItemExpand from "@/components/table/TableItemExpand.vue";
import { toLocaleFR } from "@/firebase/utils";
import { ParsedData } from "@/firebase/parse/types";

export default defineComponent({
  name: "TableItem",
  components: { TableItemExpand },
  emits: {
    onExpand: (code: unknown) => typeof code === "string",
  },
  props: {
    item: {
      type: Object as PropType<ParsedData>,
      required: true,
    },
    expandedCode: {
      type: String,
      required: false,
    },
    data: {
      type: Array as PropType<ParsedData[]>,
      required: true,
    },
    idx: {
      type: Number,
      required: true,
    },
  },
  setup(props, { emit }) {
    const eelarve = computed(() => props.item.elements[0]?.title || 0);
    const taitmine = computed(() => props.item.elements[1]?.title || 0);

    const eelarveStr = computed(() => toLocaleFR(eelarve.value, 0));
    const taitmineStr = computed(() => toLocaleFR(taitmine.value, 0));

    const percentageStr = computed(() => {
      if (eelarve.value === 0) return 0;
      return `${((taitmine.value / eelarve.value) * 100).toFixed(1)} %`;
    });

    const expand = (code: string) => {
      if (props.item.expandable) emit("onExpand", code);
    };

    const expanded = computed(
      () => props.item.expandable && props.expandedCode === props.item.group
    );

    const isSomeExpanded = computed(() => !!props.expandedCode);

    // If some row is expanded, but it is not this row
    const isGrayed = computed(() => !expanded.value && isSomeExpanded.value);

    const expandableClasses = computed(() => {
      const classes = [
        "cursor-pointer",
        "divide-x",
        "hover:bg-gray-100",
        "hover:bg-opacity-70",
        "transition",
        "duration-300",
        "ease-in-out",
      ];

      if (isGrayed.value) classes.push("text-gray-400");
      return classes;
    });

    const isNextExpandable = computed(
      () => !!props.data[props.idx + 1]?.expandable
    );

    const isBorderBottom = computed(() => {
      if (expanded.value) return true;
      if (!props.item.expandable) return false;
      return isNextExpandable.value;
    });

    const rowClasses = computed(() => {
      const classes = props.item.expandable
        ? expandableClasses.value
        : ["yellow-row bg-primary-light-orange", "text-primary-orange"];

      if (isBorderBottom.value) classes.push("border-b");

      return classes;
    });

    return {
      eelarveStr,
      taitmineStr,
      percentageStr,
      expand,
      expanded,
      rowClasses,
      isNextExpandable,
    };
  },
});
